<template>
	<div>
		<!-- Teams Section -->
		<group-dropdown 
			v-if="teams.length"
			key="teams-dropdown"
			type="team"
			:title="$t('SIDEBAR.TEAMS')"
			@create-group="createGroup('team_group')"
		>
			<div 
				class="groups-container"
				@dragover.prevent
			>
				<group
					v-for="group in teamGroups"
					:key="`group-${group.id}`"
					:group="group"
					@show-context-menu="showContextMenu($event, { itemType: '', itemId: group.id })"
				>
					<group-item
						v-for="team in group.items"
						:key="`team-${team.id}`"
						:item="team"
						item-type="team"
						:group-id="group.id"
					/>
				</group>
				
				<div class="unassigned-items">
					<group-item
						v-for="team in ungroupedTeams"
						:key="`team-${team.id}`"
						:item="team"
						item-type="team"
					/>
				</div>
			</div>

			<div class="pl-1">
				<woot-button
					v-if="isAdmin" 
					size="tiny"
					variant="clear"
					color-scheme="primary"
					icon="add"
					@click="openCreateGroupModal('team')"
				>
					{{ $t('SIDEBAR.NEW_FOLDER') }}
				</woot-button>
			</div>
		</group-dropdown>

		<!-- Inboxes Section -->
		<group-dropdown
			v-if="inboxes.length"
			key="inboxes-dropdown"
			type="inbox"
			:title="$t('SIDEBAR.INBOXES')"
			@create-group="createGroup('inbox_group')"
		>
			<group
				v-for="group in inboxGroups"
				:key="`group-${group.id}`"
				:group="group"
				@show-context-menu="showContextMenu($event, { itemType: '', itemId: group.id })"
			>
				<group-item
					v-for="inbox in group.items"
					:key="`inbox-${inbox.id}`"
					:item="inbox"
					item-type="inbox"
					:group-id="group.id"
					@show-context-menu="showContextMenu"
				/>
			</group>
			
			<div class="unassigned-items">
				<group-item
					v-for="inbox in ungroupedInboxes"
					:key="`inbox-${inbox.id}`"
					:item="inbox"
					item-type="inbox"
				/>
			</div>

			<div class="pl-1">
				<woot-button
					v-if="isAdmin" 
					size="tiny"
					variant="clear"
					color-scheme="primary"
					icon="add"
					@click="openCreateGroupModal('inbox')"
				>
					{{ $t('SIDEBAR.NEW_FOLDER') }}
				</woot-button>
			</div>
		</group-dropdown>

		<!-- Labels Section -->
		<group-dropdown
			key="labels-dropdown"
			type="label"
			:title="$t('SIDEBAR.LABELS')"
			default-open
		>
			<group
				v-for="group in labelGroups"
				:key="`group-${group.id}`"
				:group="group"
				@show-context-menu="showContextMenu($event, { itemType: '', itemId: group.id })"
			>
				<group-item
					v-for="label in group.items"
					:key="`label-${label.id}`"
					:item="label"
					item-type="label"
					:group-id="group.id"
					@show-context-menu="showContextMenu"
				/>
			</group>

			<div class="unassigned-items">
				<group-item
					v-for="label in ungroupedLabels"
					:key="`label-${label.id}`"
					:item="label"
					item-type="label"
					@show-context-menu="showContextMenu"
				/>
			</div>

			<div v-if="isAdmin" class="pl-1 flex flex-col items-start">
				<woot-button
					v-if="labels.length"
					size="tiny"
					variant="clear"
					color-scheme="primary"
					icon="add"
					data-testid="sidebar-new-label-folder-button"
					@click="openCreateGroupModal('label')"
				>
					{{ $t('SIDEBAR.NEW_FOLDER') }}
				</woot-button>
				<woot-button
					v-if="canCreateLabel"
					size="tiny"
					variant="clear"
					color-scheme="secondary"
					icon="add"
					data-testid="sidebar-new-label-button"
					@click="$emit('add-label')"
				>
					{{ $t('SIDEBAR.NEW_LABEL') }}
				</woot-button>
			</div>
		</group-dropdown>

		<!-- Modals -->
		<group-context-menu
			v-if="isAdmin"
			:is-visible="contextMenu.isVisible"
			:position="contextMenu.position"
			:item-type="contextMenu.itemType"
			:item-id="contextMenu.itemId"
			:group="selectedGroup"
			@assign="handleAssign"
			@rename="handleRename"
			@empty="handleEmpty"
			@delete="handleDelete"
			@close="closeContextMenu"
		/>

		<create-group-modal
			:show="showCreateGroupModal"
			:type="selectedGroupType"
			@close="showCreateGroupModal = false"
		/>

		<delete-confirmation-modal
      :show="showDeleteModal"
			:group="selectedGroup"
      @close="showDeleteModal = false"
      @confirm="confirmDelete"
    />

		<edit-group-modal
			:show="showEditModal"
			:group="selectedGroup"
			@close="showEditModal = false"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import adminMixin from 'dashboard/mixins/isAdmin';
import GroupDropdown from './GroupDropdown.vue';
import Group from './Group.vue';
import GroupItem from './GroupItem.vue';
import GroupContextMenu from './GroupContextMenu.vue';
import CreateGroupModal from './CreateGroupModal';
import DeleteConfirmationModal from './DeleteConfirmationModal.vue';
import EditGroupModal from './EditGroupModal';

export default {
	components: {
		GroupDropdown,
		Group,
		GroupItem,
		GroupContextMenu,
		CreateGroupModal,
    DeleteConfirmationModal,
		EditGroupModal,
	},
	mixins: [adminMixin],
	props: {
		teams: {
			type: Array,
			default: () => [],
		},
		inboxes: {
			type: Array,
			default: () => [],
		},
		labels: {
			type: Array,
			default: () => [],
		},
		canCreateLabel: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			contextMenu: {
				isVisible: false,
				position: { x: 0, y: 0 },
				itemType: '',
				itemId: null,
			},
			showCreateGroupModal: false,
			showEditModal: false,
			selectedGroup: null,
			selectedGroupType: '',
			showDeleteModal: false,
			groupToDelete: null,
		};
	},
	computed: {
		...mapGetters({
			groups: 'groups/getGroups',
		}),
		teamGroups() {
			const filteredGroups = this.groups
				.filter(group => group.group_type === 'team')
				.map(group => ({
					...group,
					items: group.items.filter(item => this.teams.some(t => t.id === item.id))
				}));

			return this.isAdmin 
				? filteredGroups 
				: filteredGroups.filter(group => group.items.length > 0);
		},
		inboxGroups() {
			const filteredGroups = this.groups
				.filter(group => group.group_type === 'inbox')
				.map(group => ({
					...group,
					items: group.items.filter(item => this.inboxes.some(t => t.id === item.id))
				}));

			return this.isAdmin 
				? filteredGroups 
				: filteredGroups.filter(group => group.items.length > 0);
		},
		labelGroups() {
			// Group items are resolved from the labels prop so only sidebar-visible labels are listed
			const filteredGroups = this.groups
				.filter(group => group.group_type === 'label')
				.map(group => ({
					...group,
					items: group.items
						.map(item => this.labels.find(label => label.id === item.id))
						.filter(Boolean)
				}));

			return this.isAdmin
				? filteredGroups
				: filteredGroups.filter(group => group.items.length > 0);
		},
		ungroupedLabels() {
			const groupedLabelIds = this.labelGroups
				.flatMap(group => group.items)
				.map(item => item.id);
			return this.labels.filter(label => !groupedLabelIds.includes(label.id));
		},
		ungroupedTeams() {
			const groupedTeamIds = this.teamGroups
				.flatMap(group => group.items)
				.map(item => item.id);
			return this.teams.filter(team => !groupedTeamIds.includes(team.id));
		},
		ungroupedInboxes() {
			const groupedInboxIds = this.inboxGroups
				.flatMap(group => group.items)
				.map(item => item.id);
			return this.inboxes.filter(inbox => !groupedInboxIds.includes(inbox.id));
		},
		deleteConfirmationMessage() {
			if (!this.selectedGroup) return '';
			
			return this.$t('GROUP_MODAL.DELETE.CONFIRM', {
				name: this.selectedGroup.name,
				count: this.selectedGroup.items.length
			});
		}
	},
	methods: {
		createGroup(groupType) {
			this.$store.dispatch('groups/create', { group_type: groupType });
		},
		openCreateGroupModal(type) {
			this.selectedGroupType = type;
			this.showCreateGroupModal = true;
		},
		showContextMenu(event, { itemType = '', itemId = null } = {}) {
			this.contextMenu = {
				isVisible: true,
				position: { x: event.clientX, y: event.clientY },
				itemType,
				itemId,
			};
		},
		closeContextMenu() {
			this.contextMenu.isVisible = false;
		},
		handleRename() {
			const group = this.groups.find(g => g.id === this.contextMenu.itemId);
			this.selectedGroup = group;
			this.showEditModal = true;
			this.closeContextMenu();
		},
		handleEmpty() {
			this.$store.dispatch('groups/clearItems', this.contextMenu.itemId);
			this.closeContextMenu();
		},
		handleDelete() {
			const group = this.groups.find(g => g.id === this.contextMenu.itemId);
			this.selectedGroup = group;
			this.showDeleteModal = true;
			this.closeContextMenu();
		},
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.selectedGroup = null;
    },
    async confirmDelete() {
      await this.$store.dispatch('groups/delete', this.selectedGroup.id);
      this.closeDeleteModal();
    },
		handleAssign(data) {
			const sourceGroup = this.groups.find(
				group =>
					group.group_type === this.contextMenu.itemType &&
					group.items.some(item => item.id === data.itemId)
			);
			if (sourceGroup?.id === data.groupId) return;

			this.$store.dispatch('groups/moveItemToGroup', {
				groupId: data.groupId,
				itemId: data.itemId,
				sourceGroupId: sourceGroup?.id,
			});
		},
	},
	mounted() {
		this.$store.dispatch('groups/get');
	}
};
</script>
